import Logger from 'lib/logger'
import mediaQuery from 'lib/media-query'

export default class VideoTeaser {
  static init ({ selector, offsetElements, columnsPerMedia, ...options }) {
    this.offsetElements = offsetElements || {}
    this.selector = selector
    this.wrappers = Array.from(document.querySelectorAll(selector))
    this.columnsPerMedia = columnsPerMedia || {}
    this.thresholdY = options.thresholdY || 50
    this.timeTreshold = options.timeTreshold || 250
    this.lastProcessedTime = Date.now()
    // Only with and height is used
    this.viewportRect = document.documentElement.getBoundingClientRect()

    setTimeout(this.initEventListeners.bind(this), 2000)
  }

  static initEventListeners () {
    this.wrappers.forEach(
      wrapper => {
        wrapper._teaser = {
          video: wrapper.querySelector('video')
        }

        wrapper.addEventListener('mouseenter', () => { this.handleMouseEnter(wrapper) })
        wrapper.addEventListener('mouseleave', () => { this.handleMouseLeave(wrapper) })
      }
    )

    window.addEventListener('touchstart', this.handleTouchStart.bind(this))
    window.addEventListener('scroll', this.handleViewPortApiScroll.bind(this))
  }

  static initIntersectionObserver () {
    if (this.intersectionObserverInitialized) return

    const observer = new IntersectionObserver(this.handleIntersection.bind(this),
      {
        rootMargin: '0px',
        threshold: 0.5
      }
    )

    this.wrappers.forEach(wrapper => observer.observe(wrapper))
    this.intersectionObserverInitialized = true
  }

  static handleIntersection (entries) {
    for (const entry of entries) {
      const { target, target: { _teaser: teaser } } = entry
      this.viewportRect = entry.rootBounds

      if (entry.intersectionRatio > 0.5) {
        !teaser.onScreen && this.handleEnterScreen(target)
      } else {
        teaser.onScreen && this.handleExitScreen(target)
      }
    }
  }

  static handleMouseEnter (wrapper) {
    this.initIntersectionObserver()
    this.playWrapper(wrapper)
  }

  static handleMouseLeave (wrapper) {
    this.pauseWrapper(wrapper)
  }

  static handleEnterScreen (wrapper) {
    const { _teaser: teaser, _teaser: { video } } = wrapper
    teaser.onScreen = true
    if (!video || teaser.loaded) return

    video.querySelectorAll('source[data-src]').forEach(
      source => {
        if (!source.getAttribute('src') && source.dataset.src) {
          source.setAttribute('src', source.dataset.src)
          delete source.dataset.src
        }
      }
    )

    video.readyState < 4 && video.load()
    teaser.loaded = true
  }

  static handleExitScreen (wrapper) {
    const { _teaser: teaser } = wrapper
    teaser.onScreen = false

    this.pauseWrapper(wrapper)
  }

  static handleTouchStart ({ changedTouches }) {
    for (const { target } of changedTouches) {
      this.onScreen.includes(target) && this.playWrapper(target)
    }
  }

  static lastProcessedPageTop = 0

  static isPageTopProcessed (pageTop) {
    if (pageTop > this.lastProcessedPageTop) {
      return this.lastProcessedPageTop + this.thresholdY > pageTop
    } else {
      return this.lastProcessedPageTop - this.thresholdY < pageTop
    }
  }

  static handleViewPortApiScroll () {
    const { scrollY } = window

    if (!this.isPageTopProcessed(scrollY) && this.lastProcessedTime + this.timeTreshold < Date.now()) {
      Logger.debug('VideoTeaser.handleViewPortApiScroll()')
      this.initIntersectionObserver()
      this.lastProcessedPageTop = scrollY
      this.lastProcessedTime = Date.now()
      this.autoplaySomething()
    }
  }

  static pauseOthers (wrapper) {
    this.onScreen.forEach(other => other !== wrapper && this.pauseWrapper(other))
  }

  static pauseWrapper (wrapper) {
    const { _teaser: { video } } = wrapper
    if (!video || video.paused) return

    wrapper.classList.remove('video_playing')
    video.classList.remove('playing')
    video.pause()
  }

  static playWrapper (wrapper) {
    const { _teaser: { video } } = wrapper
    if (!video || !video.paused) return

    this.pauseOthers(wrapper)

    if (video.readyState < 2) {
      wrapper.classList.add('loading')
      // TODO: insert spinner element

      video.addEventListener('loadeddata', () => {
        wrapper.classList.remove('loading')
        this.playVideo(video, wrapper)
      })
    } else {
      this.playVideo(video, wrapper)
    }
  }

  static playVideo (video, wrapper) {
    video.play().then(() => {
      wrapper.classList.remove('video_error')
      wrapper.classList.add('video_playing')
      video.classList.remove('error')
      video.classList.add('playing')
    }).catch(error => {
      wrapper.classList.add('video_error')
      video.classList.add('error')
      console.warn('VideoTeaser.playWrapper(): video.play() failed', error)
    })
  }

  static playOneOfTwo (leftWrapper, rightWrapper) {
    const { _teaser: { video: leftVideo } } = leftWrapper
    const { _teaser: { video: rightVideo } } = rightWrapper

    if (leftVideo && rightVideo) {
      const rect = leftWrapper.getBoundingClientRect()

      if (this.verticalCenterWithWrapperOffset(rect) < rect.y) {
        this.playWrapper(leftWrapper)
      } else {
        this.playWrapper(rightWrapper)
      }
    } else if (leftVideo || rightVideo) {
      leftVideo ? this.playWrapper(leftWrapper) : this.playWrapper(rightWrapper)
    }
  }

  static playOneOfThree (...wrappers) {
    const rect = wrappers[0].getBoundingClientRect()
    const screenY = this.verticalCenterWithWrapperOffset(rect)
    let index

    if (screenY < rect.y) {
      index = 0
    } else if (screenY - rect.height / 2 < rect.y) {
      index = 1
    } else {
      index = 2
    }

    this.playWrapper(wrappers[index])
  }

  static autoplaySomething () {
    const columns = this.numberOfColumns
    const sorted = this.sortedByCenter

    if (sorted.length === 0) return

    if (columns === 1 || sorted.length < 2) {
      this.playWrapper(sorted[0])
    } else if (columns === 2 || sorted.length < 3) {
      this.playOneOfTwo(sorted[0], sorted[1])
    } else if (columns === 3 || sorted.length >= 3) {
      const hasVideo = sorted.slice(0, 3).filter(({ _teaser: { video } }) => video)

      if (hasVideo.length === 1) {
        this.playWrapper(hasVideo[0])
      } else if (hasVideo.length === 2) {
        this.playOneOfTwo(hasVideo[0], hasVideo[1])
      } else {
        this.playOneOfThree(hasVideo[0], hasVideo[1], hasVideo[2])
      }
    } else {
      console.error(`VideoTeaser.autoplaySomething(): ${columns} columns not implemented`)
    }
  }

  static get sortedByCenter () {
    return (
      this.onScreen.sort((one, other) => {
        const oneRect = one.getBoundingClientRect()
        const otherRect = other.getBoundingClientRect()

        const a = Math.abs(this.verticalCenterWithWrapperOffset(oneRect) - oneRect.y)
        const b = Math.abs(this.verticalCenterWithWrapperOffset(otherRect) - otherRect.y)
        return a - b
      })
    )
  }

  static get onScreen () {
    return this.wrappers.filter(e => e._teaser.onScreen)
  }

  static verticalCenterWithWrapperOffset (rect) {
    return this.verticalCenter - rect.height
  }

  static _verticalCenterCache = {}

  static get verticalCenter () {
    if (this._verticalCenterCache[mediaQuery.current.name]) {
      return this._verticalCenterCache[mediaQuery.current.name]
    }

    const topOffset = this.offsetElements.top?.getBoundingClientRect()?.height || 0

    return (
      this._verticalCenterCache[mediaQuery.current.name] = this.viewportRect.height / 2 + topOffset
    )
  }

  static get numberOfColumns () {
    return this.columnsPerMedia[mediaQuery.current.name] || 1
  }
}
